import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface AppCardHeadingProps {
    // Display props
    icon?: string | React.ReactNode;
    iconColor?: string;
    iconClass?: string;
    heading?: string;
    headingBg?: string;
    styles?: string;

    // Control flags
    closeable?: string;
    reloadable?: string;
    fullScreen?: boolean;
    isFullscreen?: boolean;

    // Custom content flags
    customActions?: boolean;
    customButtons?: boolean;
    customText?: boolean;
    customComponent?: boolean;

    // Tab configuration
    withTabs?: string;
    tabs?: string;

    // Event handlers
    onToggleFullscreen?: () => void;
    onReload?: () => void;
    onClose?: () => void;
    onChangeTabCallback?: (tabKey: string) => void;

    // Content slots
    children?: React.ReactNode;
    customButtonsSlot?: React.ReactNode;
    customTextSlot?: React.ReactNode;
    customComponentSlot?: React.ReactNode;
}

const AppCardHeading: React.FC<AppCardHeadingProps> = ({
    // Display props
    icon,
    iconColor,
    iconClass,
    heading,
    headingBg,
    styles,

    // Control flags
    closeable = '',
    reloadable = '',
    fullScreen = false,
    isFullscreen = false,

    // Custom content flags
    customActions = false,
    customButtons = false,
    customText = false,
    customComponent = false,

    // Tab configuration
    withTabs = '',
    tabs = '',

    // Event handlers
    onToggleFullscreen,
    onReload,
    onClose,
    onChangeTabCallback,

    // Content slots
    children,
    customButtonsSlot,
    customTextSlot,
    customComponentSlot
}) => {
    const [activeTab, setActiveTab] = useState(0);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    // Parse tabs if it's a string
    let parsedTabs: { [key: string]: string } = {};
    if (typeof tabs === 'string' && tabs) {
        try {
            parsedTabs = JSON.parse(tabs);
        } catch (e) {
            console.warn('Failed to parse tabs string:', tabs);
        }
    }

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        };

        if (menuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuOpen]);

    const handleChangeTab = (tabKey: string) => {
        const tabIndex = parseInt(tabKey);
        setActiveTab(tabIndex);
        onChangeTabCallback?.(tabKey);
    };

    if (!heading) return null;

    // Parse inline styles if it's a string
    const inlineStyles: React.CSSProperties = {};
    if (styles) {
        try {
            const styleObj = styles.split(';').reduce((acc, style) => {
                const [property, value] = style.split(':').map((s) => s.trim());
                if (property && value) {
                    const camelProperty = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
                    acc[camelProperty] = value;
                }
                return acc;
            }, {} as any);
            Object.assign(inlineStyles, styleObj);
        } catch (e) {
            console.warn('Failed to parse styles string:', styles);
        }
    }

    return (
        <div
            className={`app-card-title ${headingBg || ''}`}
            style={{
                ...inlineStyles,
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
            }}
        >
            {icon && (
                <i
                    className={`app-card-icon material-icons app-card-icon-custom ${iconClass || ''}`}
                    style={{
                        color: iconColor,
                        flexShrink: 0
                    }}
                >
                    {icon}
                </i>
            )}

            <h3
                className="no-padding-horizontal"
                style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                    flex: 1,
                    minWidth: 0,
                    margin: 0
                }}
            >
                {heading}
            </h3>

            {!withTabs ? (
                <>
                    {(fullScreen || reloadable || closeable || customActions) && (
                        <div className="app-contextual-link col-auto px-0 text-right" style={{ flexShrink: 0 }}>
                            {fullScreen && (
                                <button className="btn btn--icon ma-0" onClick={onToggleFullscreen} type="button">
                                    <i
                                        className={`icon material-icons ${
                                            isFullscreen ? 'mdi-window-minimize' : 'mdi-window-maximize'
                                        }`}
                                        style={{ color: 'grey' }}
                                    >
                                        {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                                    </i>
                                </button>
                            )}

                            <div className="menu" ref={menuRef}>
                                <button
                                    className="btn btn--icon ma-0"
                                    onClick={() => setMenuOpen(!menuOpen)}
                                    type="button"
                                >
                                    <i className="icon material-icons">more_vert</i>
                                </button>

                                {menuOpen && (
                                    <div className="list app-card-action">
                                        {children}
                                        {reloadable && (
                                            <div
                                                className="list-item"
                                                onClick={() => {
                                                    onReload?.();
                                                    setMenuOpen(false);
                                                }}
                                            >
                                                <i className="zmdi zmdi-refresh mr-2 fs-14" />
                                                <span>{t('reload')}</span>
                                            </div>
                                        )}
                                        {closeable && (
                                            <div
                                                className="list-item"
                                                onClick={() => {
                                                    onClose?.();
                                                    setMenuOpen(false);
                                                }}
                                            >
                                                <i className="zmdi zmdi-close mr-2 fs-14" />
                                                <span>{t('close')}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {customButtons && !fullScreen && !reloadable && !closeable && !customActions && (
                        <div className="col-auto px-0 text-right" style={{ flexShrink: 0 }}>
                            {customButtonsSlot}
                        </div>
                    )}

                    {customText && !withTabs && (
                        <div>
                            <h3>{customTextSlot}</h3>
                        </div>
                    )}
                </>
            ) : (
                <div className="app-contextual-link">
                    <ul className="custom-tab-wrap list-inline">
                        {Object.entries(parsedTabs).map(([key, tabLabel]) => (
                            <li key={key} onClick={() => handleChangeTab(key)}>
                                <a
                                    href="javascript:void(0)"
                                    className={`fs-12 fw-normal px-2 py-1 rounded mx-1 ${
                                        activeTab === parseInt(key) ? 'primary white--text' : ''
                                    }`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleChangeTab(key);
                                    }}
                                >
                                    {tabLabel}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {customComponent && <>{customComponentSlot}</>}
        </div>
    );
};

export default AppCardHeading;
